<template>
    <Teleport v-if="parentDom" :to="parentDom">
        <div
            ref="markerRef"
            class="marker-house map-marker"
            :class="{ active: active === fmLatlng, hidden: data.destroyed || isShowMask }"
            @mouseenter="onMouseEnter"
            @mouseleave="onMouseLeave"
            @touchend="onClick"
            @click="onClick"
            v-outsideclick="outsideClick"
            v-outsidetouchstart="outsideClick"
            @wheel.stop
            @mousewheel.stop
            v-bind="$attrs"
        >
            <span
                v-if="data.showPrice"
                class="marker-shape"
                :class="[{ highlight: data.keepHighlight }, housesStatus, { 'pending': data.items[0].listingStatus == 'Pending' }]"
            >
                <template
                    v-if="
                        data.nodisclosureDes ||
                            hidePrice
                    "
                >&#xfe61;&#xfe61;&#xfe61;&#xfe61;&#xfe61;</template>
                <template
                    v-if="
                        !(data.nodisclosureDes ||
                            hidePrice ||
                            isShowMask)
                    "
                >
                    {{ showPrice }}
                </template>
            </span>
            <span v-else-if="!data.showPrice && data.keepHighlight" class="marker-shape" :class="[{ highlight: data.keepHighlight }, housesStatus ]">
                <template
                    v-if="
                        data.nodisclosureDes ||
                            hidePrice
                    "
                >&#xfe61;&#xfe61;&#xfe61;&#xfe61;&#xfe61;</template>
                <template
                    v-if="
                        !(data.nodisclosureDes ||
                            hidePrice ||
                            isShowMask)
                    "
                >
                    {{ showPrice }}
                </template>
            </span>
            <span v-else class="marker-shape-circle" :class="{ 'pending': data.items[0].listingStatus == 'Pending' }">
            </span>
            <Teleport to="body" :disabled="!isMobileSize">
                <div
                    v-if="showMarkerPop"
                    ref="pop"
                    class="marker-pop marker-house-pop"
                    :class="[{ bottom: popBottom, mobile: isMobileSize }, isMultiple ? 'multiple' : 'single']"
                    :style="isMobileSize ? mobilePopStyle : popStyle"
                    @click.stop="markerPopClick"
                    @touchstart="markerPopClick"
                >
                    <div v-if="isMultiple" class="house-address">
                        <i class="iconfont icon-location_02"></i>
                        <span>{{ houses[0]?.address }}</span>
                    </div>
                    <div class="marker-house-list">
                        <div v-if="isMultiple" class="house-units">
                            <span>{{ activeIndex + 1 }}/</span>
                            <span>{{ $t('md-house:md-house.unitesHere', { count: houses.length }) }}</span>
                        </div>
                        <Swiper
                            class="swiper-box"
                            :class="{ 'multiple': isMultiple }"
                            @init="onSwiperReady"
                            @slideChange="onSlideChange"
                        >
                            <SwiperSlide v-for="item in data.items" :key="item.id">
                                <HouseGrid :house="item" :showMap="true" from="map-house" />
                            </SwiperSlide>
                        </Swiper>
                        <div v-if="isMultiple" class="swiper-list-switch">
                            <span @click.stop="swiperIns.slidePrev()" class="iconfont icon-arrow_06_left" :class="{ 'disabled': activeIndex === 0 }"></span>
                            <span @click.stop="swiperIns.slideNext()" class="iconfont icon-arrow_06_right" :class="{ 'disabled': activeIndex === houses.length - 1 }"></span>
                        </div>
                    </div>
                </div>
            </Teleport>
        </div>
    </Teleport>
</template>
<script setup>
import { Swiper, SwiperSlide } from '@/common/swiper';
import { ref, computed, provide, getCurrentInstance } from 'vue';
import useMarker from '../hook/useMarker';
import HouseFormat, { getBasic } from '@/common/house';
import { getStore } from '@/store';
import useLng from '@/hooks/useLng';
import numberFormat, { formatNumberForFixed } from '@/common/format/number-format';

import { getAsyComponent } from '@/common/utils/load-module.js';

const HouseGrid = getAsyComponent(() => import('@/components/search-result/layout1/components/house-grid/house.vue'));
// 定义props
const props = defineProps({
    data: {
        type: Object,
        default: () => ({})
    },
    events: {
        type: Object,
        default: () => ({})
    },
    active: {
        type: String,
        default: ''
    }
});

// 定义emit
const emit = defineEmits(['update:active']);

// 提供isInMap
provide('isInMap', true);

// 引用DOM元素
const markerRef = ref(null);
const pop = ref(null);
const popinter = ref(null);
const parentDom = ref(null);
const mobilePopStyle = ref({ display: 'block' });

// 使用useMarker
const {
    showPlus,
    showMarkerPop,
    popStyle,
    popBottom,
    fmLatlng,
    onMouseEnter,
    onMouseLeave,
    onClick,
    outsideClick
} = useMarker({
    props,
    emit,
    parentDom,
    pop,
    popinter,
    markerRef,
    showFn: () => {
        mobilePopStyle.value = { display: 'block' };
    },
    hiddenFn: () => {
        mobilePopStyle.value = { display: 'none' };
    }
});

const keepFixed = computed(() => {
    let fixedNum = 0;
    let priceFormat = numberFormat(props.data.price, true, 0);
    if (priceFormat.indexOf('M') > -1) {
        fixedNum = 1;
    }
    return fixedNum;
});
const showPrice = computed(() => {
    if (showPlus.value) {
        return formatNumberForFixed(props.data.price, true, keepFixed.value) + '+';
    } else {
        return numberFormat(props.data.price, true, keepFixed.value);
    }
});

// 计算属性
const $t = useLng('map');
const { proxy } = getCurrentInstance();
const isMobileSize = computed(() => proxy.platform.isMobile);

const hidePrice = computed(() => {
    return !(house.value.showListingPrice || houses.value[0].showSoldPrice) && houses.value[0].soldDate;
});

const pageStore = getStore('page');
const userStore = getStore('user');

const house = computed(() => pageStore.page?.house || {});
const isShowMask = computed(() => {
    let isVerified = userStore?.userEmailVerified;
    return houses.value.length && houses.value[0].isProtected && !isVerified;
});
const houses = computed(() => {
    const vm = { platform: proxy.platform, $t };
    const page = pageStore.processedGlobalData;
    return props.data.items.map(item => {
        let house = HouseFormat.call(vm, item, page);
        house.basic = getBasic.call(vm, item, page);
        return house;
    });
});

const isMultiple = computed(() => props.data.items.length > 1);
const housesStatus = computed(() => {
    let houses = props.data.items;
    if (!houses.length) {
        return '';
    }
    let status = houses[0].listingStatus || '';
    return 'house-status-' + status.toLowerCase();
});

const markerPopClick = (e) => {
    e.outSideIgnore = outsideClick;
}

const swiperIns = ref(null);
const activeIndex = ref(0);
const onSwiperReady = (swiper) => {
    swiperIns.value = swiper;
}
const onSlideChange = (swiper) => {
    activeIndex.value = swiper.activeIndex;
}

</script>
<style lang="scss">
@import '@/theme/mixin.scss';
@import '@/components/map/css/map-common.scss';
.site-map.layout1 .marker-house .marker-house-pop {
    background-color: var(--g-bg-color);
    &.multiple {
        width: 450px;
    }
    &.mobile {
        width: 240px;
    }
}

.marker-house-pop .search-result-house-grid.map-house {
    width: 100%;
    .house-status-container {
        top: 10px;
        left: 10px;
    }
    .icon-fav {
        top: 10px;
        right: 10px;
    }
    .house-detail {
        padding: 10px 10px 2px;
        .house-mls-logo {
            max-width: 60px;
            max-height: 16px;
            position: absolute;
            left: 10px;
            bottom: calc(100% + 10px);
            margin-left: 0;
        }
    }
    .container-top {
        height: 150px;
        .img-preview {
            object-fit: cover;
        }
    }
}
.marker-house-pop.mobile {
    position:fixed;
    bottom: 90px;
    left: 15px;
    width: calc(100% - 30px);
    background-color: var(--g-bg-color);
}
.marker-house-pop.single {
    .has-mls-logo .container-top {
        position: relative;
        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 50px;
            background: linear-gradient(180deg, rgba(25, 25, 25, 0) 0%, rgba(25, 25, 25, 0.8) 66.96%);
            pointer-events: none;
        }
    } 
}
.marker-house-pop.multiple {
    .house-address {
        display: flex;
        align-items: center;
        line-height: 18px;
        font-size: 14px;
        font-weight: 500;
        color: var(--g-primary-color);
        padding: 15px;
        border-bottom: 1px solid rgba(218, 218, 218, 0.5);
        i {
            margin-right: 10px;
        }
        span {
            @include webkitBox(2);
            flex: 1;
        }
    }
    .marker-house-list {
        max-height: 280px;
        overflow-y: auto;
    }
    .house-units {
        display: flex;
        line-height: 18px;
        font-size: 12px;
        color: var(--g-text-color);
        padding: 20px 15px 0;
    }
    .search-result-house-grid.map-house {
        padding: 15px 15px 0 15px;
        @include color-button-hover();
        article {
            flex-direction: row;
            padding-bottom: 15px;
            border-bottom: 1px solid rgba(218, 218, 218, 0.5);
        }
        .container-top {
            width: 140px;
            height: 90px;
        }
        .house-status-container {
            display: none;
            // top: 25px;
            // width: 100px;
        }
        .icon-fav {
            top: 50%;
            right: 0;
            transform: translateY(-50%);
            &.collected {
                .icon-heart {
                    display: none;
                }
                .icon-heart-fill {
                    display: block;
                }
            }
            .icon-heart-fill {
                display: none;
            }
            .icon-heart {
                color: #191919;
            }
            .iconfont {
                left: auto;
            }
        }
        .house-address {
            display: none;
        }
        .house-detail {
            padding: 0 10px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            .house-mls-logo {
                left: -130px;
                bottom: 10px;
            }
            .house-basic {
                margin: 0;
            }
            .house-info {
                margin-top: 0;
            }
        }
    }
    .search-result-house-grid + .search-result-house-grid {
        border-top: 1px solid rgba(218, 218, 218, 0.5);
    }
}
.swiper-list-switch {
    @include swiper-list-switch();
}
</style>
